import { createBrowserRouter, redirect } from "react-router";
import { PageNotFound } from "./app/404";
import { ErrorPage } from "./app/500";
import { SignUpPage } from "./app/auth/sign-up/page";
import { AutomationsPage } from "./app/automations/page";
import { AutomationPrSizeLabelerPage } from "./app/automations/settings/pr-size-labeler/page";
import { AutomationIncidentDetectionPage } from "./app/automations/settings/incident-detection/page";
import { AutomationPrTitleCheckPage } from "./app/automations/settings/pr-title-check/page";
import { GithubInstallPage } from "./app/github/install/page";
import { OAuthGithubPage } from "./app/github/oauth/page";
import { HomePage } from "./app/home/page";
import { HumansPage } from "./app/humans/page";
import { PersonCodeReviewsPage } from "./app/humans/people/[handle]/code-reviews/page";
import { PersonOverviewPage } from "./app/humans/people/[handle]/overview/page";
import { PersonPage } from "./app/humans/people/[handle]/page";
import { PersonPullRequestsPage } from "./app/humans/people/[handle]/pull-requests/page";
import { PeoplePage } from "./app/humans/people/page";
import { TeamAlertsPage } from "./app/humans/teams/[id]/alerts/page";
import { MergedWithoutApprovalAlertPage } from "./app/humans/teams/[id]/alerts/settings/merged-without-approval/page";
import { SlowMergeAlertPage } from "./app/humans/teams/[id]/alerts/settings/slow-merge/page";
import { SlowReviewAlertPage } from "./app/humans/teams/[id]/alerts/settings/slow-review/page";
import { TeamDigestsPage } from "./app/humans/teams/[id]/digests/page";
import { TeamMetricsDigestPage } from "./app/humans/teams/[id]/digests/settings/team-metrics/page";
import { TeamWipDigestPage } from "./app/humans/teams/[id]/digests/settings/team-wip/page";
import { TeamMembersPage } from "./app/humans/teams/[id]/members/page";
import { TeamPage } from "./app/humans/teams/[id]/page";
import { TeamPullRequestsPage } from "./app/humans/teams/[id]/pull-requests/page";
import { TeamWorkInProgressPage } from "./app/humans/teams/[id]/work-in-progress/page";
import { TeamWorkLogPage } from "./app/humans/teams/[id]/work-log/page";
import { TeamsPage } from "./app/humans/teams/page";
import { DoraPage } from "./app/metrics-and-insights/dora/page";
import { AppPage } from "./app/page";
import { BillingPage } from "./app/settings/billing/page";
import { IntegrationsPage } from "./app/settings/integrations/page";
import { IntegrationSlackPage } from "./app/settings/integrations/slack/page";
import { MyAccountPage } from "./app/settings/my-account/page";
import { SettingsPage } from "./app/settings/page";
import { PullRequestSettingsPage } from "./app/settings/pull-request-settings/page";
import { PullRequestSizePage } from "./app/settings/pull-request-settings/size/page";
import { UsersSettingsPage } from "./app/settings/users/page";
import { WorkspaceSettingsPage } from "./app/settings/workspace/page";
import { SystemsPage } from "./app/systems/page";
import { RepositoriesPage } from "./app/systems/repositories/page";
import {
  isAuthError,
  redirectIfCredentials,
  redirectIfNoCredentials,
} from "./providers/auth.provider";
import { isSandboxMode } from "./sandbox/sandbox-context";
import {
  handleOAuthRedirect,
  installGitAppIfNoWorkspaces,
} from "./providers/github.provider";
import { showInfoNotification } from "./providers/notification.provider";
import { loadUserWithWorkspaces } from "./providers/workspace.provider";
import { DeploymentsPage } from "./app/systems/deployments/page";
import { ApplicationsPage } from "./app/systems/applications/page";
import { IncidentsPage } from "./app/systems/incidents/page";
import { ApplicationsCreatePage } from "./app/systems/applications/upsert/create/page";
import { ApplicationsEditPage } from "./app/systems/applications/upsert/edit/page";
import { EnvironmentsPage } from "./app/systems/environments/page";
import { IncidentsCreatePage } from "./app/systems/incidents/upsert/create/page";
import { IncidentsEditPage } from "./app/systems/incidents/upsert/edit/page";
import { DeploymentsViewPage } from "./app/systems/deployments/view/page";
import { DoraFailureRatePage } from "./app/metrics-and-insights/dora/trends/failure-rate/page";
import { DoraDeploymentFrequencyPage } from "./app/metrics-and-insights/dora/trends/deployment-frequency/page";
import { DoraLeadTimePage } from "./app/metrics-and-insights/dora/trends/lead-time/page";
import { DoraMttrPage } from "./app/metrics-and-insights/dora/trends/mttr/page";
import { WorkspaceResyncPage } from "./app/settings/workspace/resync/page";
import { SystemsPullRequestsPage } from "./app/systems/pull-requests/page";
import { MetricsAndInsightsPage } from "./app/metrics-and-insights/page";
import { PrFlowPage } from "./app/metrics-and-insights/pr-flow/page";
import { CodeReviewEfficiencyPage } from "./app/metrics-and-insights/code-review-efficiency/page";
import { LoginPage } from "./app/auth/login/page";

export const router = createBrowserRouter([
  {
    children: [
      {
        // Always accessible pages
        children: [
          {
            path: "/github",
            children: [
              {
                loader: handleOAuthRedirect,
                path: "/github/callback",
                element: <OAuthGithubPage />,
              },
              {
                path: "/github/install",
                element: <GithubInstallPage />,
              },
            ],
          },
        ],
      },
      // Public-only pages
      {
        loader: redirectIfCredentials,
        children: [
          {
            path: "/login",
            element: <LoginPage />,
          },
          {
            path: "/sign-up",
            element: <SignUpPage />,
          },
        ],
      },
      // Sandbox entry point — starts MSW, sets fake auth, redirects to /
      {
        path: "/sandbox",
        lazy: async () => {
          const { sandboxLoader } = await import("./sandbox/sandbox-provider");
          return { loader: sandboxLoader };
        },
      },
      // Authenticated pages
      {
        element: <AppPage />,
        errorElement: (
          <AppPage>
            <ErrorPage />
          </AppPage>
        ),
        loader: async ({ request }) => {
          if (isSandboxMode()) {
            const { ensureSandboxWorker } =
              await import("./sandbox/sandbox-provider");
            await ensureSandboxWorker();
          }

          const redirectResponse = redirectIfNoCredentials(
            new URL(request.url),
          );

          if (redirectResponse) {
            showInfoNotification({
              title: "Session expired",
              message: "Please authenticate your credentials again.",
            });

            return redirectResponse;
          }

          try {
            await loadUserWithWorkspaces();
          } catch (error) {
            if (isAuthError(error)) {
              return redirect("/login");
            }

            throw error;
          }

          return installGitAppIfNoWorkspaces();
        },
        children: [
          {
            path: "/",
            element: <HomePage />,
          },
          {
            path: "/systems/",
            element: <SystemsPage />,
            children: [
              {
                path: "/systems/repositories",
                element: <RepositoriesPage />,
              },
              {
                path: "/systems/applications",
                element: <ApplicationsPage />,
                children: [
                  {
                    path: "/systems/applications/new",
                    element: <ApplicationsCreatePage />,
                  },
                  {
                    path: "/systems/applications/edit/:applicationId",
                    element: <ApplicationsEditPage />,
                  },
                ],
              },
              {
                path: "/systems/pull-requests",
                element: <SystemsPullRequestsPage />,
              },
              {
                path: "/systems/deployments",
                element: <DeploymentsPage />,
                children: [
                  {
                    path: "/systems/deployments/view/:deploymentId",
                    element: <DeploymentsViewPage />,
                  },
                ],
              },
              {
                path: "/systems/incidents",
                element: <IncidentsPage />,
                children: [
                  {
                    path: "/systems/incidents/new",
                    element: <IncidentsCreatePage />,
                  },
                  {
                    path: "/systems/incidents/edit/:incidentId",
                    element: <IncidentsEditPage />,
                  },
                ],
              },
              {
                path: "/systems/environments",
                element: <EnvironmentsPage />,
              },
            ],
          },
          {
            path: "/settings",
            element: <SettingsPage />,
            children: [
              {
                path: "/settings/workspace",
                element: <WorkspaceSettingsPage />,
                children: [
                  {
                    path: "/settings/workspace/resync",
                    element: <WorkspaceResyncPage />,
                  },
                ],
              },
              {
                path: "/settings/billing",
                element: <BillingPage />,
              },
              {
                path: "/settings/pull-request",
                element: <PullRequestSettingsPage />,
                children: [
                  {
                    path: "/settings/pull-request/size",
                    element: <PullRequestSizePage />,
                  },
                ],
              },
              {
                path: "/settings/users",
                element: <UsersSettingsPage />,
              },
              {
                path: "/settings/my-account",
                element: <MyAccountPage />,
              },
              {
                path: "/settings/integrations",
                element: <IntegrationsPage />,
                children: [
                  {
                    path: "/settings/integrations/slack",
                    element: <IntegrationSlackPage />,
                  },
                ],
              },
            ],
          },
          {
            path: "metrics-and-insights",
            element: <MetricsAndInsightsPage />,
            children: [
              {
                path: "/metrics-and-insights/dora",
                element: <DoraPage />,
                children: [
                  {
                    path: "/metrics-and-insights/dora/deployment-frequency",
                    element: <DoraDeploymentFrequencyPage />,
                  },
                  {
                    path: "/metrics-and-insights/dora/lead-time",
                    element: <DoraLeadTimePage />,
                  },
                  {
                    path: "/metrics-and-insights/dora/failure-rate",
                    element: <DoraFailureRatePage />,
                  },
                  {
                    path: "/metrics-and-insights/dora/mttr",
                    element: <DoraMttrPage />,
                  },
                ],
              },
              {
                path: "/metrics-and-insights/pr-flow",
                element: <PrFlowPage />,
              },
              {
                path: "/metrics-and-insights/code-review-efficiency",
                element: <CodeReviewEfficiencyPage />,
              },
            ],
          },
          {
            path: "/humans",
            element: <HumansPage />,
            children: [
              {
                path: "/humans/teams",
                element: <TeamsPage />,
              },
              {
                path: "/humans/teams/:teamId",
                element: <TeamPage />,
                children: [
                  {
                    path: "/humans/teams/:teamId/wip",
                    element: <TeamWorkInProgressPage />,
                  },
                  {
                    path: "/humans/teams/:teamId/work-log",
                    element: <TeamWorkLogPage />,
                  },
                  {
                    path: "/humans/teams/:teamId/members",
                    element: <TeamMembersPage />,
                  },
                  {
                    path: "/humans/teams/:teamId/pull-requests",
                    element: <TeamPullRequestsPage />,
                  },
                  {
                    path: "/humans/teams/:teamId/alerts",
                    element: <TeamAlertsPage />,
                    children: [
                      {
                        path: "/humans/teams/:teamId/alerts/slow-merge",
                        element: <SlowMergeAlertPage />,
                      },
                      {
                        path: "/humans/teams/:teamId/alerts/slow-review",
                        element: <SlowReviewAlertPage />,
                      },
                      {
                        path: "/humans/teams/:teamId/alerts/merged-without-approval",
                        element: <MergedWithoutApprovalAlertPage />,
                      },
                    ],
                  },
                  {
                    path: "/humans/teams/:teamId/digests",
                    element: <TeamDigestsPage />,
                    children: [
                      {
                        path: "/humans/teams/:teamId/digests/metrics",
                        element: <TeamMetricsDigestPage />,
                      },
                      {
                        path: "/humans/teams/:teamId/digests/wip",
                        element: <TeamWipDigestPage />,
                      },
                    ],
                  },
                ],
              },
              {
                path: "/humans/people",
                element: <PeoplePage />,
              },
              {
                path: "/humans/people/:handle",
                element: <PersonPage />,
                children: [
                  {
                    path: "/humans/people/:handle",
                    element: <PersonOverviewPage />,
                  },
                  {
                    path: "/humans/people/:handle/pull-requests",
                    element: <PersonPullRequestsPage />,
                  },
                  {
                    path: "/humans/people/:handle/code-reviews",
                    element: <PersonCodeReviewsPage />,
                  },
                ],
              },
            ],
          },
          {
            path: "automations",
            element: <AutomationsPage />,
            children: [
              {
                path: "/automations/pr-title-check",
                element: <AutomationPrTitleCheckPage />,
              },
              {
                path: "/automations/pr-size-labeler",
                element: <AutomationPrSizeLabelerPage />,
              },
              {
                path: "/automations/incident-detection",
                element: <AutomationIncidentDetectionPage />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <PageNotFound />,
  },
]);
